import React, { useState, useEffect } from 'react';
import { 
  Card, Button, Space, List, message, Row, Col, 
  Select, Modal, Input, Badge, Spin, Tooltip
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, PlayCircleOutlined,
  CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined,
  CopyOutlined
} from '@ant-design/icons';
import { ethers } from 'ethers';

// 接口定义
interface NodeEntry {
  id: number;
  url: string;
  isCustom: boolean;
  status: 'idle' | 'testing' | 'success' | 'error';
  latency: number | null;
  blockNumber: number | null;
}

interface ChainConfig {
  name: string;
  key: string;
  symbol: string;
  explorer: string;
}

// 支持的链配置
const CHAIN_CONFIGS: ChainConfig[] = [
  {
    name: 'Ethereum',
    key: 'ethereum',
    symbol: 'ETH',
    explorer: 'https://etherscan.io'
  },
  {
    name: 'Polygon',
    key: 'polygon',
    symbol: 'MATIC',
    explorer: 'https://polygonscan.com'
  },
  {
    name: 'BSC',
    key: 'bsc',
    symbol: 'BNB',
    explorer: 'https://bscscan.com'
  },
  {
    name: 'Arbitrum',
    key: 'arbitrum',
    symbol: 'ETH',
    explorer: 'https://arbiscan.io'
  },
  {
    name: 'Optimism',
    key: 'optimism',
    symbol: 'ETH',
    explorer: 'https://optimistic.etherscan.io'
  },
  {
    name: 'Base',
    key: 'base',
    symbol: 'ETH',
    explorer: 'https://basescan.org'
  }
];

// 预设节点数据
const PRESET_NODES: Record<string, string[]> = {
  ethereum: [
    'https://geth.mytokenpocket.vip',
    'https://eth-mainnet.public.blastapi.io',
    'https://1rpc.io/eth',
    'https://rpc.ankr.com/eth',
    'https://eth.llamarpc.com',
    'https://eth.api.onfinality.io/public',
    'https://ethereum.publicnode.com',
    'https://rpc.flashbots.net',
    'https://rpc.mevblocker.io',
    'https://eth.public-rpc.com',
    'https://eth-mainnet.g.alchemy.com/v2/demo',
    'https://mainnet.infura.io/v3/9aa3d95b3bc440fa88ea12eaa4456161'
  ],
  polygon: [
    'https://polygon-rpc.com',
    'https://node.onekey.so/polygon',
    'https://polygon-bor-rpc.publicnode.com',
    'https://rpc-mainnet.matic.quiknode.pro',
    'https://polygon.llamarpc.com',
    'https://matic-mainnet.chainstacklabs.com',
    'https://polygon.blockpi.network/v1/rpc/public',
    'https://rpc.ankr.com/polygon',
    'https://polygon-mainnet.g.alchemy.com/v2/demo',
    'https://polygon.publicnode.com'
  ],
  bsc: [
    'https://bsc-dataseed.binance.org',
    'https://bsc-dataseed1.binance.org',
    'https://bsc-dataseed2.binance.org',
    'https://bsc-dataseed3.binance.org',
    'https://bsc-dataseed4.binance.org',
    'https://bsc-dataseed1.defibit.io',
    'https://bsc-dataseed2.defibit.io',
    'https://1rpc.io/bnb',
    'https://rpc.ankr.com/bsc',
    'https://bsc.publicnode.com',
    'https://bsc-mainnet.nodereal.io/v1/64a9df0874fb4a93b9d0a3849de012d3'
  ],
  arbitrum: [
    'https://arb1.arbitrum.io/rpc',
    'https://rpc.ankr.com/arbitrum',
    'https://arbitrum.llamarpc.com',
    'https://1rpc.io/arb',
    'https://endpoints.omniatech.io/v1/arbitrum/one/public',
    'https://arbitrum.blockpi.network/v1/rpc/public',
    'https://arb.mytokenpocket.vip',
    'https://arbitrum-mainnet.infura.io/v3/9aa3d95b3bc440fa88ea12eaa4456161',
    'https://arbitrum.publicnode.com'
  ],
  optimism: [
    'https://mainnet.optimism.io',
    'https://rpc.ankr.com/optimism',
    'https://optimism-mainnet.infura.io/v3/9aa3d95b3bc440fa88ea12eaa4456161',
    'https://optimism.publicnode.com'
  ],
  base: [
    'https://base.blockpi.network/v1/rpc/public',
    'https://base.meowrpc.com',
    'https://1rpc.io/base',
    'https://developer-access-mainnet.base.org',
    'https://mainnet.base.org',
    'https://base.publicnode.com',
    'https://rpc.ankr.com/base',
    'https://base-mainnet.g.alchemy.com/v2/demo'
  ]
};

const RpcSpeedTest: React.FC = () => {
  // 状态管理
  const [nodeList, setNodeList] = useState<NodeEntry[]>([]);
  const [selectedChain, setSelectedChain] = useState<string>('ethereum');
  const [isTesting, setIsTesting] = useState<boolean>(false);
  
  // Modal 状态
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [newNodeUrl, setNewNodeUrl] = useState('');

  // 样式定义
  const buttonStyle = {
    borderRadius: '6px',
    fontWeight: '500',
    fontSize: '14px',
    height: '36px',
    minWidth: '80px'
  };

  const primaryButtonStyle = {
    ...buttonStyle,
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    border: 'none',
    color: 'white',
    boxShadow: '0 2px 4px rgba(102, 126, 234, 0.2)'
  };

  const componentStyle = {
    borderRadius: '6px',
    fontSize: '14px'
  };

  // 获取当前链配置
  const getCurrentChainConfig = (): ChainConfig => {
    return CHAIN_CONFIGS.find(chain => chain.key === selectedChain) || CHAIN_CONFIGS[0];
  };

  // 从localStorage加载自定义节点
  const loadCustomNodes = (chainKey: string): string[] => {
    try {
      const stored = localStorage.getItem(`rpc_custom_nodes_${chainKey}`);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('加载自定义节点失败:', error);
      return [];
    }
  };

  // 保存自定义节点到localStorage
  const saveCustomNodes = (chainKey: string, nodes: string[]) => {
    try {
      localStorage.setItem(`rpc_custom_nodes_${chainKey}`, JSON.stringify(nodes));
    } catch (error) {
      console.error('保存自定义节点失败:', error);
    }
  };

  // 初始化节点列表
  const initializeNodeList = (chainKey: string) => {
    const presetNodes = PRESET_NODES[chainKey] || [];
    const customNodes = loadCustomNodes(chainKey);
    
    const allNodes: NodeEntry[] = [];
    let id = 1;

    // 添加预设节点
    presetNodes.forEach(url => {
      allNodes.push({
        id: id++,
        url,
        isCustom: false,
        status: 'idle',
        latency: null,
        blockNumber: null
      });
    });

    // 添加自定义节点
    customNodes.forEach(url => {
      allNodes.push({
        id: id++,
        url,
        isCustom: true,
        status: 'idle',
        latency: null,
        blockNumber: null
      });
    });

    setNodeList(allNodes);
  };

  // 链切换处理
  const handleChainChange = (chainKey: string) => {
    setSelectedChain(chainKey);
    initializeNodeList(chainKey);
  };

  // 删除自定义节点
  const handleDeleteCustomNode = (nodeId: number) => {
    const nodeToDelete = nodeList.find(node => node.id === nodeId);
    if (!nodeToDelete || !nodeToDelete.isCustom) return;

    // 从节点列表中移除
    const updatedNodeList = nodeList.filter(node => node.id !== nodeId);
    setNodeList(updatedNodeList);

    // 从localStorage中移除
    const customNodes = loadCustomNodes(selectedChain);
    const updatedCustomNodes = customNodes.filter(url => url !== nodeToDelete.url);
    saveCustomNodes(selectedChain, updatedCustomNodes);

    message.success('自定义节点已删除');
  };

  // 验证URL格式
  const isValidUrl = (url: string): boolean => {
    try {
      const urlObj = new URL(url);
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
    } catch {
      return false;
    }
  };

  // 复制节点URL到剪贴板
  const handleCopyUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      message.success('节点URL已复制到剪贴板');
    } catch (error) {
      // 降级方案：使用传统方法复制
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        message.success('节点URL已复制到剪贴板');
      } catch (err) {
        message.error('复制失败，请手动复制');
      }
      document.body.removeChild(textArea);
    }
  };

  // 批量复制所有节点URL
  const handleCopyAllUrls = async () => {
    const allUrls = nodeList.map(node => node.url).join('\n');
    try {
      await navigator.clipboard.writeText(allUrls);
      message.success(`已复制${nodeList.length}个节点URL到剪贴板`);
    } catch (error) {
      // 降级方案：使用传统方法复制
      const textArea = document.createElement('textarea');
      textArea.value = allUrls;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        message.success(`已复制${nodeList.length}个节点URL到剪贴板`);
      } catch (err) {
        message.error('复制失败，请手动复制');
      }
      document.body.removeChild(textArea);
    }
  };

  // 复制成功的节点URL
  const handleCopySuccessUrls = async () => {
    const successNodes = nodeList.filter(node => node.status === 'success');
    if (successNodes.length === 0) {
      message.warning('没有测试成功的节点');
      return;
    }

    // 按延迟排序，最快的在前面
    const sortedNodes = successNodes.sort((a, b) => (a.latency || 0) - (b.latency || 0));
    const successUrls = sortedNodes.map(node => node.url).join('\n');

    try {
      await navigator.clipboard.writeText(successUrls);
      message.success(`已复制${successNodes.length}个成功节点URL到剪贴板（按速度排序）`);
    } catch (error) {
      // 降级方案：使用传统方法复制
      const textArea = document.createElement('textarea');
      textArea.value = successUrls;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        message.success(`已复制${successNodes.length}个成功节点URL到剪贴板（按速度排序）`);
      } catch (err) {
        message.error('复制失败，请手动复制');
      }
      document.body.removeChild(textArea);
    }
  };

  // 添加自定义节点
  const handleAddCustomNode = () => {
    const trimmedUrl = newNodeUrl.trim();

    if (!trimmedUrl) {
      message.error('请输入RPC节点URL');
      return;
    }

    if (!isValidUrl(trimmedUrl)) {
      message.error('请输入有效的URL格式');
      return;
    }

    // 检查是否已存在
    const existingNode = nodeList.find(node => node.url === trimmedUrl);
    if (existingNode) {
      message.error('该节点已存在');
      return;
    }

    // 添加到节点列表
    const newNode: NodeEntry = {
      id: Math.max(...nodeList.map(n => n.id), 0) + 1,
      url: trimmedUrl,
      isCustom: true,
      status: 'idle',
      latency: null,
      blockNumber: null
    };

    const updatedNodeList = [...nodeList, newNode];
    setNodeList(updatedNodeList);

    // 保存到localStorage
    const customNodes = loadCustomNodes(selectedChain);
    const updatedCustomNodes = [...customNodes, trimmedUrl];
    saveCustomNodes(selectedChain, updatedCustomNodes);

    // 重置Modal状态
    setNewNodeUrl('');
    setIsAddModalVisible(false);
    message.success('自定义节点添加成功');
  };

  // 单个节点测试函数
  const testNode = async (node: NodeEntry): Promise<void> => {
    try {
      const startTime = performance.now();
      const provider = new ethers.JsonRpcProvider(node.url);

      // 设置超时时间为10秒
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('请求超时')), 10000);
      });

      const blockNumberPromise = provider.getBlockNumber();
      const blockNumber = await Promise.race([blockNumberPromise, timeoutPromise]) as number;

      const endTime = performance.now();
      const latency = Math.round(endTime - startTime);

      // 更新节点状态为成功
      setNodeList(prev => prev.map(n =>
        n.id === node.id
          ? { ...n, status: 'success', latency, blockNumber }
          : n
      ));
    } catch (error) {
      console.error(`节点 ${node.url} 测试失败:`, error);

      // 更新节点状态为失败
      setNodeList(prev => prev.map(n =>
        n.id === node.id
          ? { ...n, status: 'error', latency: null, blockNumber: null }
          : n
      ));
    }
  };

  // 核心测速逻辑
  const handleStartTest = async () => {
    if (nodeList.length === 0) {
      message.warning('没有可测试的节点');
      return;
    }

    // 启动测试
    setIsTesting(true);
    message.info('开始测试所有RPC节点...');

    // 重置所有节点状态
    setNodeList(prev => prev.map(node => ({
      ...node,
      status: 'testing',
      latency: null,
      blockNumber: null
    })));

    try {
      // 并发测试所有节点
      const testPromises = nodeList.map(node => testNode(node));
      await Promise.allSettled(testPromises);

      message.success('所有节点测速完成!');
    } catch (error) {
      console.error('测速过程出错:', error);
      message.error('测速过程中出现错误');
    } finally {
      setIsTesting(false);
    }
  };

  // 组件初始化
  useEffect(() => {
    initializeNodeList(selectedChain);
  }, []);

  return (
    <div className="container-responsive">
      {/* 标题 */}
      <h1 style={{ 
        textAlign: 'center', 
        fontSize: 'var(--font-xl)',
        fontWeight: 'bold',
        marginBottom: 'var(--spacing-2xl)',
        color: '#333'
      }}>
        RPC节点测速工具
      </h1>

      {/* 顶部控制区域 */}
      <Card className="spacing-responsive">
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={8}>
            <Space>
              <span style={{ fontWeight: '500' }}>选择链:</span>
              <Select
                value={selectedChain}
                onChange={handleChainChange}
                style={{ width: 200, ...componentStyle }}
                disabled={isTesting}
              >
                {CHAIN_CONFIGS.map(chain => (
                  <Select.Option key={chain.key} value={chain.key}>
                    {chain.name} ({chain.symbol})
                  </Select.Option>
                ))}
              </Select>
            </Space>
          </Col>
          
          <Col xs={24} sm={12} md={8} style={{ textAlign: 'center' }}>
            <Button
              type="primary"
              size="large"
              icon={<PlayCircleOutlined />}
              onClick={handleStartTest}
              disabled={nodeList.length === 0 || isTesting}
              loading={isTesting}
              style={{
                ...primaryButtonStyle,
                minWidth: '160px',
                height: '44px',
                fontSize: '16px'
              }}
            >
              {isTesting ? '测速中...' : '开始测速'}
            </Button>
          </Col>

          <Col xs={24} sm={24} md={8} style={{ textAlign: 'right' }}>
            <Space direction="vertical" size="small">
              <div style={{ fontSize: '13px', color: '#666' }}>
                节点总数: <strong>{nodeList.length}</strong> |
                成功: <strong style={{ color: '#52c41a' }}>{nodeList.filter(n => n.status === 'success').length}</strong> |
                失败: <strong style={{ color: '#ff4d4f' }}>{nodeList.filter(n => n.status === 'error').length}</strong>
              </div>
              {nodeList.some(n => n.status === 'success') && (
                <div style={{ fontSize: '12px', color: '#666' }}>
                  最快节点: <strong style={{ color: '#1890ff' }}>
                    {Math.min(...nodeList.filter(n => n.latency !== null).map(n => n.latency!))} ms
                  </strong>
                </div>
              )}
              <Space size="small">
                <Button
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={handleCopyAllUrls}
                  disabled={nodeList.length === 0}
                  style={{
                    ...buttonStyle,
                    fontSize: '12px',
                    height: '28px',
                    minWidth: '90px'
                  }}
                >
                  复制全部
                </Button>
                <Button
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={handleCopySuccessUrls}
                  disabled={nodeList.filter(n => n.status === 'success').length === 0}
                  style={{
                    ...buttonStyle,
                    fontSize: '12px',
                    height: '28px',
                    minWidth: '90px',
                    color: '#52c41a',
                    borderColor: '#52c41a'
                  }}
                >
                  复制成功
                </Button>
              </Space>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* 节点列表区域 */}
      <Card title={`${getCurrentChainConfig().name} RPC节点列表`} className="spacing-responsive">
        <List
          itemLayout="horizontal"
          dataSource={nodeList}
          renderItem={(item) => (
            <List.Item
              extra={
                <div style={{ textAlign: 'right', minWidth: '200px' }}>
                  {item.status === 'testing' && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      <Spin size="small" />
                      <span style={{ marginLeft: '8px', color: '#1890ff' }}>测试中...</span>
                    </div>
                  )}

                  {item.status === 'success' && (
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginBottom: '4px' }}>
                        <Badge status="success" />
                        <span style={{ fontWeight: '600', color: '#52c41a', fontSize: '16px' }}>
                          {item.latency} ms
                        </span>
                        {/* 标记最快节点 */}
                        {nodeList.filter(n => n.status === 'success').length > 0 &&
                         item.latency === Math.min(...nodeList.filter(n => n.latency !== null).map(n => n.latency!)) && (
                          <span style={{
                            marginLeft: '6px',
                            fontSize: '12px',
                            color: '#ff4d4f',
                            fontWeight: 'bold'
                          }}>
                            🚀
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        区块高度: {item.blockNumber?.toLocaleString()}
                      </div>
                    </div>
                  )}

                  {item.status === 'error' && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      <Badge status="error" />
                      <span style={{ color: '#ff4d4f', fontWeight: '500' }}>连接失败</span>
                    </div>
                  )}

                  {item.status === 'idle' && (
                    <div style={{ color: '#999', fontSize: '13px' }}>
                      等待测试
                    </div>
                  )}
                </div>
              }
            >
              <List.Item.Meta
                title={
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Tooltip title={item.url}>
                      <span style={{
                        maxWidth: '350px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: '14px'
                      }}>
                        {item.url}
                      </span>
                    </Tooltip>
                    <Button
                      type="text"
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={() => handleCopyUrl(item.url)}
                      style={{
                        marginLeft: '8px',
                        color: '#1890ff',
                        padding: '0 4px'
                      }}
                      disabled={isTesting}
                    />
                    {item.isCustom && (
                      <Button
                        type="text"
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => handleDeleteCustomNode(item.id)}
                        style={{
                          marginLeft: '4px',
                          color: '#ff4d4f',
                          padding: '0 4px'
                        }}
                        disabled={isTesting}
                      />
                    )}
                  </div>
                }
                description={
                  <div style={{ fontSize: '12px', color: '#999' }}>
                    {item.isCustom ? '自定义节点' : '预设节点'}
                  </div>
                }
              />
            </List.Item>
          )}
        />

        {/* 添加节点按钮 */}
        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={() => setIsAddModalVisible(true)}
            style={{
              ...buttonStyle,
              minWidth: '140px',
              height: '40px'
            }}
            disabled={isTesting}
          >
            添加自定义节点
          </Button>
        </div>
      </Card>

      {/* 添加节点Modal */}
      <Modal
        title="添加自定义RPC节点"
        open={isAddModalVisible}
        onOk={handleAddCustomNode}
        onCancel={() => {
          setIsAddModalVisible(false);
          setNewNodeUrl('');
        }}
        okText="添加"
        cancelText="取消"
        okButtonProps={{
          style: primaryButtonStyle,
          disabled: !newNodeUrl.trim()
        }}
      >
        <div style={{ margin: '20px 0' }}>
          <Input
            placeholder="请输入RPC节点URL，例如: https://your-rpc-url.com"
            value={newNodeUrl}
            onChange={(e) => setNewNodeUrl(e.target.value)}
            onPressEnter={handleAddCustomNode}
            style={componentStyle}
          />
          <div style={{ marginTop: '8px', fontSize: '12px', color: '#999' }}>
            支持HTTP和HTTPS协议的RPC节点URL
          </div>
        </div>
      </Modal>

      {/* 使用提示 */}
      <Card size="small" style={{
        background: '#f6ffed',
        border: '1px solid #b7eb8f',
        marginTop: '20px'
      }}>
        <div style={{ color: '#52c41a' }}>
          <strong>💡 使用提示：</strong>
          <ul style={{ margin: '8px 0 0 20px', paddingLeft: 0 }}>
            <li>支持测试多个主流EVM链的RPC节点延迟和同步状态</li>
            <li>延迟越低表示网络连接越快，区块高度越高表示节点同步越及时</li>
            <li>🚀 标记表示当前最快的节点，点击复制按钮可快速复制节点URL</li>
            <li>可以批量复制所有节点或仅复制测试成功的节点（按速度排序）</li>
            <li>可以添加自定义RPC节点进行测试，自定义节点会自动保存</li>
            <li>测试采用并发方式，所有节点同时进行测试以提高效率</li>
            <li>测试超时时间为10秒，超时的节点会标记为连接失败</li>
          </ul>
        </div>
      </Card>
    </div>
  );
};

export default RpcSpeedTest;
